// Libraries
import { Card, Center, Flex, Text } from '@chakra-ui/react';
import * as Sentry from '@sentry/nextjs';
import { useEffect } from 'react';
// Components
import { ErrorPageCard } from '@molecules/index';
// Utils
import translation from '@translations/en.json';
// Types
import { ErrorBoundaryProps } from '../../ErrorBoundary.type';

const text = translation.error_card;

/**
 * A fallback component for rendering within an error boundary.
 *
 * @component
 * @param {Omit<ErrorBoundaryProps, 'children'>} props - The props for the Fallback component.
 * @param {boolean} [props.global] - Determines whether to display the global error layout.
 * @param {boolean} [props.card] - Determines whether to display the error as a Card component.
 * @returns {ReactElement} The rendered Fallback component.
 */
export const Fallback = ({ global, card, error }: Omit<ErrorBoundaryProps, 'children'>) => {
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  if (global) {
    return <ErrorPageCard />;
  }

  if (card) {
    return (
      <Card h='full'>
        <Center flex={1} p={4}>
          <Flex direction='column'>
            <Text fontSize='xl' fontWeight='bold' mt={4} textAlign='center'>
              {text.error.title}
            </Text>
            <Text fontSize='s' mt={4} textAlign='center'>
              {text.error.description}
            </Text>
          </Flex>
        </Center>
      </Card>
    );
  }

  return (
    <Flex direction='column' p={4}>
      <Text fontSize='xl' fontWeight='bold' mt={4} textAlign='center'>
        {text.error.title}
      </Text>
      <Text fontSize='s' mt={4} textAlign='center'>
        {text.error.description}
      </Text>
    </Flex>
  );
};
